/* eslint-disable no-console */
/* eslint-disable no-unused-vars */
import exponentialDelayFactory from './exponentialDelay';
import Logger, { isNode } from './IsomorphicLogger';
import { getCallerLocation } from './Logger';
import { getFilename } from './esm';
import { defer } from './defer';

// Timers for use with later (keyed timers)
const laterTimers = {};

// Track ALL active timer IDs for clearAllLater()
let allActiveTimerIds = [];

// For awaiting all promises in scripts
let laterWaitList = [];

// Open later() scopes (see beginLaterScope). While a scope is registered here,
// every later() call ALSO registers its entry into the scope, so a caller can
// await/flush ONLY the deferred work it triggered — not the process-global
// backlog. This is the bounded alternative to waitForAllLater() for test
// cleanup (the esign bastion flake, 2026-07: a global drain in afterEach never
// bounds under a concurrent test batch because other suites keep queueing into
// the same global queue).
const laterScopes = new Set();

// MATM-1605: which scopes CAUSED a later() call. beginLaterScope() enters its
// scope into the current async context, and later() adds its entry only to
// the open scopes in that context. A timer armed before a scope opened (e.g.
// another suite's _shared-base.js broadcast debounce) keeps the context it was
// armed in, so its later() is no longer captured by an unrelated scope.
// getBuiltinModule is synchronous and absent in browsers (and on old Node),
// where we fall back to the old rule: every open scope captures every later().
const AsyncLocalStorageCtor =
	globalThis.process?.getBuiltinModule?.('node:async_hooks')?.AsyncLocalStorage;
const laterScopeContext = AsyncLocalStorageCtor
	? new AsyncLocalStorageCtor()
	: undefined;

/** The open scopes a later() call made right now should register with. */
const scopesForNewLater = () => {
	if (!laterScopeContext) {
		return [...laterScopes];
	}
	return (laterScopeContext.getStore() || []).filter((scope) =>
		laterScopes.has(scope),
	);
};

/**
 * Traps and handles errors that occur during asynchronous operations.
 * Useful for wrapping promises where you want errors logged and alerted
 * without crashing the process.
 *
 * @template T
 * @param {(() => T | Promise<T>) | Promise<T>} callbackOrPromise - Function to call or promise to await
 * @param {import('./later').TrapAsyncErrorsOptions} [options] - Error handling configuration
 * @returns {Promise<T | Error>} Promise resolving to the result, or the error if one occurred
 *
 * @example
 * // Using a callback function
 * const result = await trapAsyncErrors(() => {
 *   // Asynchronous code here
 * }, {
 *   logger,
 *   alertLabel: 'MyOperation-Failed',
 * });
 *
 * // Using a promise
 * const result = await trapAsyncErrors(somePromise);
 */
export const trapAsyncErrors = async (
	callbackOrPromise,
	{
		// The stack trace to be used for logging purposes.
		laterStack = new Error('stack trace please').stack,
		// The logger instance to be used for error logging.
		logger = Logger,
		//  Whether to disable the logger alert.
		disableLoggerAlert = false,
		// The name of the calling method, used in error messages and alerts. (We'll automatically use code location of caller if not given)
		outerMethodName = undefined,
		// The label for the Slack alert.
		alertLabel = undefined,
		// The callback to be invoked when an error occurs.
		// If provided, will be used as the alert label for the logger alert. If a function, will be called with the error and the alert label will be the return value.
		onError = undefined,
	} = {},
) => {
	// Get location (outside of this file) where the trapAsyncErrors call originated
	const { file, line } = getCallerLocation({
		excludes: [getFilename(import.meta.url)],
	});

	// Build short string for file location and line number
	const codeLocation = [file, line].filter(Boolean).join(':');

	// // ONLY FOR TESTING
	// // eslint-disable-next-line no-console
	// console.log(`>>> trapAsyncErrors/codeLocation`, codeLocation);

	// If user didn't provide informative details, we'll try to help
	// by using the code location for the alert label that goes to slack
	if (!outerMethodName && !alertLabel) {
		// eslint-disable-next-line no-param-reassign
		alertLabel = `${codeLocation}/AsyncErrorTrap`;
	}

	let result;
	try {
		const promise =
			typeof callbackOrPromise === 'function'
				? callbackOrPromise()
				: callbackOrPromise;

		if (promise && typeof promise.catch === 'function') {
			result = await promise.catch((ex) => {
				result = ex;

				const context = {
					message: ex?.message,
					errorStack: ex?.stack,
					laterStack,
					originalException: ex,
				};

				// Guard against child/tagged loggers that omit `.error` — otherwise
				// calling it inside this trap promotes a swallowable error into an
				// unguarded TypeError from inside the handler whose whole purpose is
				// not to throw (BDL-2694). Same guard as the outer-catch branch below.
				if (typeof logger.error === 'function') {
					logger.error(
						`Trapped error in promise given ${
							outerMethodName ? `to ${outerMethodName}` : `at ${codeLocation}`
						}:`,
						context,
					);
					if (!isNode) {
						logger.error(ex);
					}
				}

				// .alert is only relevant on the server. Guard against child/tagged
				// loggers that omit `.alert` — otherwise calling it inside this
				// trap promotes a swallowable error into an unhandled rejection
				// (bastion 2026-06-08: esign `later()` crashed mocha this way).
				// Also honour `disableLoggerAlert` (BDL-2694) — same guard as the
				// outer-catch branch below.
				if (
					isNode &&
					!disableLoggerAlert &&
					typeof logger.alert === 'function'
				) {
					logger.alert(
						typeof alertLabel === 'function'
							? alertLabel(ex)
							: alertLabel ||
									`AsyncErrorTrap-PromiseRejected-${outerMethodName}`,
						context,
					);
				}

				if (onError) {
					onError(ex);
				}

				return ex;
			});
		}
	} catch (ex) {
		result = ex;

		const context = {
			message: ex?.message,
			errorStack: ex?.stack,
			laterStack,
			originalException: ex,
		};

		// console.error(`\n\n>>>odd logger`, logger, new Error('odd from').stack);
		// Guard against child/tagged loggers that omit `.error` (BDL-2694) — same
		// guard as the rejection branch above.
		if (typeof logger.error === 'function') {
			logger.error(
				`Trapped error caught by trapAsyncErrors() in promise given ${
					outerMethodName ? `to ${outerMethodName}` : `at ${codeLocation}`
				}:`,
				context,
			);
		}

		// .alert is only relevant on the server. Same guard as the rejection
		// branch above — sub-loggers without `.alert` must not crash the trap.
		if (isNode && !disableLoggerAlert && typeof logger.alert === 'function') {
			logger.alert(
				typeof alertLabel === 'function'
					? alertLabel(ex)
					: alertLabel || `AsyncErrorTrap-CaughtException-${outerMethodName}`,
				context,
			);
		}

		if (onError) {
			onError(ex);
		}
	}

	return result;
};

// Arbitrary delay to move 'later' further out from UX-relevant interactions
// const DelayFactory = () => {
// 	return 100 + Math.ceil(Math.random() * 200);
// };

const DelayFactory = exponentialDelayFactory({
	initialDelay: 100,
	maxDelay: 3333,
	multiplier: 1.125,
});

/**
 * Simple utility to move a given promise out of the event loop.
 * Useful for fire-and-forget operations where you still want error catching.
 *
 * Features:
 * - Delays execution to avoid blocking UX-relevant code
 * - Traps and logs errors without crashing
 * - Supports debouncing via the `key` option
 * - Returns a clearable promise
 *
 * @template T
 * @param {(() => T | Promise<T>) | Promise<T>} callbackOrPromise - Function to call or promise to await
 * @param {import('./later').LaterOptions | number} [options] - Configuration options, or just a number for delay
 * @returns {import('./later').LaterPromise<void>} Promise with a .clear() method to cancel execution
 *
 * @example
 * // Fire and forget with error catching
 * later(() => saveAnalytics(data));
 *
 * // Debounced operation - subsequent calls with same key cancel previous
 * later(() => saveDocument(), { key: 'autosave', delay: 1000 });
 *
 * // Immediate execution with error trapping
 * await later(() => fetchData(), { immediate: true });
 *
 * // Clear a pending operation
 * const promise = later(() => expensiveOp(), { key: 'myOp' });
 * promise.clear(); // or later.clear('myOp');
 */
export const later = (callbackOrPromise, options) => {
	const { stack: laterStack } = new Error('later called from...');

	const finished = defer();

	const factoryDelay = DelayFactory();

	let {
		logger = Logger,
		delay = factoryDelay,
		key,
		immediate = false,
		debugDelay = false,
		...rest
	} = typeof options === 'number' ? { delay: options } : options || {};

	if (laterTimers[key]) {
		clearTimeout(laterTimers[key]);
	}

	if (immediate) {
		return trapAsyncErrors(callbackOrPromise, {
			...rest,
			outerMethodName: 'later',
			laterStack,
			logger,
		});
	}

	if (debugDelay) {
		logger.debug(` > Using delay: `, delay);
	}

	let timerId;

	// The scope entry lets an open later() scope (see beginLaterScope) both
	// await AND proactively flush THIS specific deferred chain, independent of
	// the process-global laterWaitList. `flush()` runs the work immediately
	// (skipping the remaining UX delay) so a scoped waiter's completion time is
	// bounded by its OWN work, not by unrelated pending later() timers.
	// `laterStack` + `createdAt` let a timed-out settleLaterScope() NAME the
	// job it was waiting on (MATM-1601) instead of hanging anonymously.
	const entry = {
		finished,
		flush: undefined,
		laterStack,
		createdAt: Date.now(),
	};

	// Guard so the work runs at most once, whether triggered by the timer
	// firing or by an explicit flush() from settleLaterScope().
	let ran = false;
	const run = async () => {
		if (ran) {
			return finished;
		}
		ran = true;

		// Cancel the pending timer (a no-op when we ARE the timer firing) and
		// drop this timer id from the active list.
		clearTimeout(timerId);
		allActiveTimerIds = allActiveTimerIds.filter((id) => id !== timerId);

		await trapAsyncErrors(callbackOrPromise, { ...rest, laterStack, logger });
		finished.resolve();

		// Lower the accumulated delay pressure, otherwise we'll always stay at the highest
		// delay after a few later() calls
		DelayFactory.reduce();

		// Remove from the global wait list and every scope that captured us.
		laterWaitList = laterWaitList.filter((x) => x !== finished);
		laterScopes.forEach((scope) => scope.delete(entry));

		return finished;
	};
	entry.flush = run;

	timerId = setTimeout(run, delay);

	// Track this timer ID for clearAllLater()
	allActiveTimerIds.push(timerId);

	if (key) {
		laterTimers[key] = timerId;
	}

	laterWaitList.push(finished);

	// Register with the open later() scopes that caused this call, so a scoped
	// waiter can settle only the work it triggered.
	scopesForNewLater().forEach((scope) => scope.add(entry));

	const returnPromise = Promise.resolve(finished);
	returnPromise.clear = () => {
		clearTimeout(timerId);
		allActiveTimerIds = allActiveTimerIds.filter((id) => id !== timerId);
		laterTimers[key] = null;
		laterWaitList = laterWaitList.filter((x) => x !== finished);
		laterScopes.forEach((scope) => scope.delete(entry));
	};

	return returnPromise;
};

/**
 * Clears a pending later() operation by key, timer reference, or timer ID.
 * Useful for canceling scheduled operations before they execute.
 *
 * @param {string | number | import('./later').LaterPromise<unknown>} keyOrTimerRefOrId - The key string, timer ID number, or LaterPromise object
 *
 * @example
 * // Clear by key
 * later.clear('autosave');
 *
 * // Clear by returned promise
 * const ref = later(() => doWork(), { key: 'task' });
 * later.clear(ref);
 */
later.clear = (keyOrTimerRefOrId) => {
	if (typeof keyOrTimerRefOrId === 'string') {
		// Logger.debug(`[later.clear] Clearing timer for key:`, keyOrTimerRefOrId);
		clearTimeout(laterTimers[keyOrTimerRefOrId]);
		laterTimers[keyOrTimerRefOrId] = null;
	} else if (typeof keyOrTimerRefOrId === 'number') {
		// Logger.debug(`[later.clear] Clearing timer for ID:`, keyOrTimerRefOrId);
		clearTimeout(keyOrTimerRefOrId);
	} else if (
		typeof keyOrTimerRefOrId === 'object' &&
		typeof keyOrTimerRefOrId.clear === 'function'
	) {
		// Logger.debug(
		// 	`[later.clear] Clearing timer for .clear-able:`,
		// 	keyOrTimerRefOrId,
		// );
		keyOrTimerRefOrId.clear();
	}
};

/**
 * Waits for all pending later() operations to complete, including any
 * new later() calls that are queued while waiting (drain behavior).
 * Useful in scripts or tests to ensure all background work finishes.
 *
 * @returns {Promise<void[]>} Promise that resolves when all pending later() calls complete
 *
 * @example
 * later(() => task1());
 * later(() => task2());
 * await waitForAllLater();
 * console.log('All tasks complete');
 *
 * @example
 * // Also handles cascading later() calls
 * later(() => {
 *   later(() => nestedTask()); // This will also be awaited
 * });
 * await waitForAllLater(); // Waits for both tasks
 */
export const waitForAllLater = async () => {
	const allResults = [];

	// Safety limit to prevent infinite loops if something keeps queuing later() calls
	const maxIterations = 100;
	let iterations = 0;

	// Keep draining until the queue is truly empty
	// (resolved promises remove themselves, but may queue new ones)
	while (laterWaitList.length > 0) {
		if (iterations >= maxIterations) {
			Logger.warn(
				`[waitForAllLater] Hit max iterations (${maxIterations}), ${laterWaitList.length} items still pending - stopping drain`,
			);
			break;
		}

		// Wait for all currently queued items
		// Note: new items may be added to laterWaitList while we wait,
		// and completed items remove themselves from the list
		// eslint-disable-next-line no-await-in-loop -- intentional drain pattern: must wait for batch before checking for new items
		const results = await Promise.all(laterWaitList);
		allResults.push(...results);
		iterations++;
	}

	return allResults;
};

/**
 * Cancels all pending later() operations without waiting for them.
 * Useful in tests to clean up background work that shouldn't continue after tests exit.
 * Unlike waitForAllLater(), this immediately cancels all pending timers.
 *
 * @returns {number} The number of timers that were cleared
 *
 * @example
 * later(() => task1());
 * later(() => task2());
 * clearAllLater(); // Both tasks are cancelled
 */
export const clearAllLater = () => {
	const count = allActiveTimerIds.length;

	// Clear all active timers
	allActiveTimerIds.forEach((timerId) => {
		clearTimeout(timerId);
	});

	// Clear keyed timers
	Object.keys(laterTimers).forEach((key) => {
		clearTimeout(laterTimers[key]);
		laterTimers[key] = null;
	});

	// Reset tracking arrays
	allActiveTimerIds = [];
	laterWaitList = [];

	// Drop all scope-captured entries too — their timers are cleared above, so
	// a concurrent settleLaterScope() must not try to flush cancelled work.
	laterScopes.forEach((scope) => scope.clear());

	return count;
};

/**
 * Default time bound for settleLaterScope(). Kept BELOW the 180s hook timeout
 * MatM suites run with (MATM-1600) so the bounded settle rejects first and the
 * caller's restore/cleanup still runs inside its own hook. A healthy settle
 * takes milliseconds; only a job that would otherwise hang trips it.
 */
export const DEFAULT_SETTLE_LATER_SCOPE_TIMEOUT_MS = 60000;

/**
 * The first stack frame of a later() call that is NOT inside this module, i.e.
 * the code that scheduled the job. Used only to label a pending job when a
 * bounded settle times out.
 */
const laterCallSite = (laterStack) => {
	const frames = String(laterStack || '')
		.split('\n')
		.slice(1)
		.map((line) => line.trim());
	const caller = frames.find((line) => !/[/\\]later\.[jt]s:/.test(line));
	return caller || frames[0] || 'unknown call site';
};

const MAX_PENDING_JOBS_DESCRIBED = 10;

const describePendingScope = (scope, timeoutMs) => {
	const now = Date.now();
	const entries = [...scope];
	const lines = entries
		.slice(0, MAX_PENDING_JOBS_DESCRIBED)
		.map(
			(entry) =>
				`  - ${laterCallSite(entry.laterStack)} (queued ${
					now - entry.createdAt
				}ms ago)`,
		);
	if (entries.length > MAX_PENDING_JOBS_DESCRIBED) {
		lines.push(
			`  - ...and ${entries.length - MAX_PENDING_JOBS_DESCRIBED} more`,
		);
	}
	return [
		`[settleLaterScope] timed out after ${timeoutMs}ms with ${entries.length} scoped later() job(s) still pending:`,
		...lines,
	].join('\n');
};

/**
 * Opens an isolated later() scope. While the returned scope is open, every
 * later() call made from the scope's async context (the code after this call,
 * and timers, promises and callbacks it starts) also registers its deferred
 * work into the scope, so the caller can later await (and flush) ONLY the work
 * it triggered — independent of unrelated pending later() timers queued
 * elsewhere. A timer armed BEFORE the scope opened is not captured even if it
 * fires while the scope is open (MATM-1605). In a runtime without
 * AsyncLocalStorage (browsers) every later() made while the scope is open is
 * captured instead.
 *
 * This is the bounded replacement for the waitForAllLater() global-drain
 * anti-pattern in test cleanup. waitForAllLater() blocks on the WHOLE
 * process-global queue, so under a concurrent test batch other suites keep it
 * from ever bounding — structurally exceeding even large this.timeout() caps
 * (the esign bastion Timeout flake, recurring 2026-07). A scope bounds cleanup
 * to the test's OWN deferred chains, so the drain time no longer depends on
 * batch load.
 *
 * Usage (mocha):
 *   let laterScope;
 *   beforeEach(() => { laterScope = beginLaterScope(); });
 *   afterEach(async () => { await settleLaterScope(laterScope); ...cleanup... });
 *
 * @returns {Set<object>} An opaque scope handle. Pass it to settleLaterScope().
 */
export const beginLaterScope = () => {
	const scope = new Set();
	laterScopes.add(scope);
	if (laterScopeContext) {
		// Keep enclosing scopes that are still open, so a nested scope's work
		// is captured by both (as before). Closed ones are dropped so a long-
		// lived context does not accumulate dead scopes.
		laterScopeContext.enterWith([...scopesForNewLater(), scope]);
	}
	return scope;
};

/**
 * Settles an isolated later() scope opened by beginLaterScope(): flushes and
 * awaits ONLY the later() work triggered while the scope was open — including
 * cascaded later() calls those callbacks queue — then closes the scope.
 * Flushing runs each deferred immediately (skipping any remaining UX delay) so
 * completion time is bounded by the work itself, not by timer delays or the
 * global backlog.
 *
 * Safe to call with a null/undefined scope (no-op) so an afterEach hook doesn't
 * need to guard the case where a beforeEach failed before opening the scope.
 *
 * @param {Set<object>} scope - The handle returned by beginLaterScope().
 * Rejects (and still closes the scope) if the drain has not finished within
 * `timeoutMs`, naming each pending job's later() call site and age. Pass
 * `timeoutMs: Infinity` (or 0) for the old unbounded behaviour.
 *
 * Pass `keepOpen: true` to drain WITHOUT closing, e.g. from a mid-test helper.
 * Do not re-open with beginLaterScope() inside such a helper instead: a scope
 * opened in an async helper is not in its caller's async context, so the
 * caller's later work would not be captured (MATM-1605).
 *
 * @param {{ maxIterations?: number, timeoutMs?: number, keepOpen?: boolean }} [options] - Cascade-drain pass cap, time bound (default DEFAULT_SETTLE_LATER_SCOPE_TIMEOUT_MS), and whether to leave the scope open after a successful drain.
 * @returns {Promise<void>}
 */
export const settleLaterScope = async (
	scope,
	{
		maxIterations = 100,
		timeoutMs = DEFAULT_SETTLE_LATER_SCOPE_TIMEOUT_MS,
		keepOpen = false,
	} = {},
) => {
	if (!scope) {
		return;
	}

	let timedOut = false;

	const drain = async () => {
		let iterations = 0;

		// Keep flushing until the scope is empty. Completed entries remove
		// themselves; a flushed callback may queue new later() calls, which land
		// in this still-open scope and get flushed on the next pass.
		while (scope.size > 0 && !timedOut) {
			if (iterations >= maxIterations) {
				Logger.warn(
					`[settleLaterScope] Hit max iterations (${maxIterations}), ${scope.size} scoped item(s) still pending - stopping drain`,
				);
				break;
			}

			// eslint-disable-next-line no-await-in-loop -- intentional cascade drain: flush the current batch before checking for newly-queued work
			await Promise.all([...scope].map((entry) => entry.flush()));
			iterations += 1;
		}
	};

	// MATM-1601: maxIterations bounds PASSES, not time. One captured job whose
	// promise never settles used to hang this await until the mocha hook
	// timeout, skipping the caller's sinon.restore() and poisoning the next
	// suite. Race the drain against a time bound that REJECTS, naming what is
	// still pending, so the hook fails fast and loudly instead.
	let timer;
	const bounded = Number.isFinite(timeoutMs) && timeoutMs > 0;
	try {
		if (!bounded) {
			await drain();
			return;
		}
		await Promise.race([
			drain(),
			new Promise((_resolve, reject) => {
				timer = setTimeout(() => {
					timedOut = true;
					reject(new Error(describePendingScope(scope, timeoutMs)));
				}, timeoutMs);
			}),
		]);
	} finally {
		clearTimeout(timer);
		// Close the scope so post-settle later() calls (e.g. from teardown)
		// don't accumulate against a handle nobody will drain - also on timeout,
		// even with keepOpen.
		if (!keepOpen || timedOut) {
			laterScopes.delete(scope);
		}
	}
};

/**
 * Closes a later() scope WITHOUT flushing or awaiting its pending work.
 * Useful in an `after`/`afterAll` hook to guarantee a per-test scope is not
 * left registered if the last settle was skipped. Pending timers still fire on
 * their own schedule (or are cleared by clearAllLater()).
 *
 * @param {Set<object>} scope - The handle returned by beginLaterScope().
 * @returns {void}
 */
export const endLaterScope = (scope) => {
	if (scope) {
		laterScopes.delete(scope);
	}
};

/**
 * Creates a later() function with a pre-configured logger.
 * Useful when you want all later() calls in a module to use
 * a specific logger instance.
 *
 * @param {import('./later').LaterLogger} logger - Logger instance to inject into all calls
 * @returns {<T>(callback: (() => T | Promise<T>) | Promise<T>, options?: Omit<import('./later').LaterOptions, 'logger'>) => import('./later').LaterPromise<void>} A later() function that uses the provided logger
 *
 * @example
 * const myLater = getLaterWithLogger(myLogger);
 * myLater(() => doSomething()); // Uses myLogger for error logging
 */
export const getLaterWithLogger = (logger) => (callback, options) =>
	later(callback, { ...options, logger });
